import React, { useState } from "react";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, OAuthProvider, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebaseConfig";
import { Form, Button, Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";
import { Utils } from "../../common/Utils";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const { settings, userloaded } = useGlobal();

  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoggingIn(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      console.log("User logged in successfully");
    } catch (err: any) {
      setError(err.message);
      console.error("Login Error:", err);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setLoggingIn(true);

    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      console.log("Google login successful");
    } catch (err: any) {
      setError(err.message);
      console.error("Google Login Error:", err);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError(null);
    setLoggingIn(true);
    const provider = new OAuthProvider("microsoft.com");

    try {
      await signInWithPopup(auth, provider);
      console.log("Microsoft login successful");
    } catch (err: any) {
      setError(err.message);
      console.error("Microsoft Login Error:", err);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleForgotPassword = async () => {
    setResetMessage(null);
    setError(null);

    if (!email) {
      setError("Please enter your email to reset your password.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setResetMessage("Password reset email sent! Check your inbox.");
    } catch (err: any) {
      setError(err.message);
      console.error("Password Reset Error:", err);
    }
  };

  if (settings.isDefault)
    return (      <Container className="mt-4 text-center">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Container>);

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col xs={12} md={6}>
          <h2 className="text-center mb-4">Sign In</h2>

          {error && <Alert variant="danger">{error}</Alert>}
          {resetMessage && <Alert variant="success">{resetMessage}</Alert>}
          {/* {settings && <> settings.allowEmailLogin && <> */}
          {settings && <>
            <Form onSubmit={handleEmailPasswordLogin}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Row>
                  <Col>
                    <Form.Label>Password</Form.Label></Col>
                  <Col className="text-end">
                    <Button variant="link" onClick={handleForgotPassword} className="p-0">
                      Forgot Password?
                    </Button>
                  </Col>
                </Row>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </Form.Group>

              <Button type="submit" variant="primary" disabled={!userloaded} className="w-100">
                {loggingIn ? <Spinner animation="border" size="sm" /> : "Login"}
              </Button>
            </Form>

            <hr />
          </>}
          {settings && settings.allowGoogleLogin &&
            <Button
              onClick={handleGoogleLogin}
              variant="outline-secondary"
              disabled={loggingIn}
              className="w-100 d-flex align-items-center justify-content-center gap-2"
            >
              {loggingIn ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <img src="/google.png" alt="Google" width={20} height={20} />
                  Sign In with Google
                </>
              )}
            </Button>
          }
          {settings && settings.allowMicrosoftLogin &&
            <Button
              onClick={handleMicrosoftLogin}
              variant="outline-secondary"
              disabled={loggingIn}
              className="w-100 mt-2 d-flex align-items-center justify-content-center gap-2"
            >
              {loggingIn ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <img src="/microsoft.png" alt="Microsoft" width={20} height={20} />
                  Sign In with Microsoft
                </>
              )}
            </Button>
          }

          {settings && settings.allowEmailLogin &&
            <div className="text-center mt-3">
              <Link to={Utils.GetUrl("/register")} >Don't have an account? Register here</Link>
            </div>
          }

        </Col>
      </Row>
    </Container>
  );
};

export default Login;